import type { Lesson } from '../schema.js';

// Mahjong Solitaire lessons: explain + quiz only. The facts match the game as built
// (packages/game-engine/src/mahjong-solitaire/): you clear a stack of tiles by removing matching pairs, a tile is
// free when nothing lies on top of it and its left or right side is open, two tiles match when they show the same
// picture, or are any two flowers, or any two seasons, the full board is the 144-tile "turtle" with a smaller flat
// board and a pyramid for beginners, every board can be cleared, and there is a hint, an undo and a shuffle. This
// is the one-player tile game only: the four-player Mahjong table game is NOT taught here. These rules are recalled,
// not checked against a rulebook, and a player should confirm them.
export const MAHJONG_SOLITAIRE_LESSONS: Lesson[] = [
  {
    id: 'mahjong-solitaire-free-tiles',
    slug: 'mahjong-solitaire-free-tiles-and-pairs',
    title: 'Mahjong Solitaire: free tiles and pairs',
    audienceLevel: 'beginner',
    concepts: ['mahjong-solitaire'],
    steps: [
      {
        kind: 'explain',
        title: 'Remove matching pairs',
        body: 'Mahjong Solitaire starts with a stack of tiles. Your goal is to clear every tile by taking away matching pairs, two at a time. You can only pick a free tile. A tile is free when nothing is lying on top of it and either its left side or its right side is open. Two free tiles that show the same picture are a pair. The four flower tiles all match each other, even though each looks different, and so do the four season tiles.',
        narration: 'Pick two free tiles with the same picture and they disappear. Clear the whole board to win.',
      },
      {
        kind: 'quiz',
        title: 'Free tiles',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'When is a tile free?',
            choices: [
              'Nothing is on top of it, and its left or its right side is open',
              'It is in the top row of the board',
              'It has a tile on each side',
            ],
            correct: 0,
          },
          {
            prompt: 'Which two tiles can you remove as a pair?',
            choices: ['Two free tiles with the same picture', 'Any two free tiles', 'Two tiles that are next to each other'],
            correct: 0,
          },
          {
            prompt: 'Two different flower tiles are both free. Can you match them?',
            choices: ['Yes, all flowers match each other', 'No, they look different'],
            correct: 0,
          },
        ],
      },
    ],
  },
  {
    id: 'mahjong-solitaire-helps',
    slug: 'mahjong-solitaire-boards-and-helpers',
    title: 'Mahjong Solitaire: boards and helpers',
    audienceLevel: 'beginner',
    concepts: ['mahjong-solitaire'],
    steps: [
      {
        kind: 'explain',
        title: 'Pick a board, ask for help',
        body: 'You can choose a board. The flat board is a single layer of tiles, the pyramid stacks a second layer on top, and the turtle is the big classic board with 144 tiles stacked in five layers. Every board can be cleared, so there is always a way to win. If you are not sure what to do, ask for a hint and it shows you a pair you can remove. You can undo your last move as many times as you like. If no pairs are left, you can shuffle the tiles that remain. After a shuffle you cannot undo back past it.',
        narration: 'Choose flat, pyramid or turtle. Use a hint if you are stuck, and undo if you change your mind.',
      },
      {
        kind: 'quiz',
        title: 'Boards and helpers',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'Which board is the big classic one with 144 tiles?',
            choices: ['The turtle', 'The flat board', 'The pyramid'],
            correct: 0,
          },
          {
            prompt: 'You are not sure which pair to take. What can you use?',
            choices: ['A hint', 'A restart that keeps your score', 'Nothing'],
            correct: 0,
          },
          {
            prompt: 'You made a move and changed your mind. What can you do?',
            choices: ['Undo it', 'Nothing, it is final'],
            correct: 0,
          },
        ],
      },
    ],
  },
];
